/**
 * Correspondência de procedimentos entre a consulta e o catálogo.
 *
 * Os procedimentos do catálogo costumam vir prefixados com o nome do
 * profissional ("Ana Maria Ferreira - ECG...") e duplicados por convênio
 * (uma linha por `insurance_id`). Estas funções resolvem o nome escrito nas
 * observações do agendamento para a linha correta do catálogo.
 */

export interface ProfessionalRef {
  id?: string;
  full_name?: string;
}

export interface ProcedureMatchOptions {
  name: string;
  /** Outras grafias aceitáveis do mesmo procedimento (ex.: a linha original da observação). */
  altNames?: string[];
  code?: string;
  professional?: ProfessionalRef | null;
  insuranceId?: string;
  particularOnly?: boolean;
}

export const normalizeProcedureMatchName = (value: string): string =>
  String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

export const procedureBelongsToProfessional = (
  proc: any,
  professional: ProfessionalRef | null | undefined,
): boolean => {
  if (!professional) return false;
  const procProfId = proc?.metadata?.professional_id ? String(proc.metadata.professional_id) : '';
  if (professional.id && procProfId && procProfId === String(professional.id)) return true;

  const profName = normalizeProcedureMatchName(professional.full_name || '');
  if (!profName) return false;
  const procName = normalizeProcedureMatchName(proc?.name || '');
  if (!procName) return false;

  if (procName.startsWith(`${profName} -`) || procName.startsWith(`${profName}-`)) return true;

  const dashIdx = String(proc?.name || '').indexOf(' - ');
  if (dashIdx > 0) {
    const prefix = normalizeProcedureMatchName(String(proc.name).slice(0, dashIdx));
    if (prefix === profName) return true;
  }
  return false;
};

/** Nomes comparáveis de um procedimento: completo e sem o prefixo do profissional. */
const procedureComparableNames = (proc: any): string[] => {
  const names = new Set<string>();
  const full = normalizeProcedureMatchName(proc?.name || '');
  if (full) names.add(full);

  const metaBase = normalizeProcedureMatchName(proc?.metadata?.procedure_base_name || '');
  if (metaBase) names.add(metaBase);

  const raw = String(proc?.name || '');
  const dashIdx = raw.indexOf(' - ');
  if (dashIdx > 0) {
    const afterPrefix = normalizeProcedureMatchName(raw.slice(dashIdx + 3));
    if (afterPrefix) names.add(afterPrefix);
  }
  return Array.from(names);
};

export interface ParsedAppointmentProcedure {
  /** Nome sem o código no final, quando havia um código. */
  name: string;
  /** Linha original (nomes de catálogo podem conter parênteses). */
  raw: string;
  code?: string;
}

/** Só tratamos o final entre parênteses como código quando parece um código. */
const looksLikeProcedureCode = (value: string) => /^[A-Za-z0-9.\-/]{2,20}$/.test(value);

/**
 * Procedimentos escritos nas observações do agendamento.
 * O seletor da agenda grava uma linha por procedimento, no formato
 * "Nome do procedimento (CÓDIGO)".
 */
export const parseProcedureLinesFromNotes = (notes?: string | null): ParsedAppointmentProcedure[] => {
  if (!notes) return [];
  const seen = new Set<string>();
  const parsed: ParsedAppointmentProcedure[] = [];

  for (const rawLine of String(notes).split(/\r?\n/)) {
    const line = rawLine.replace(/^[\s•*]+/, '').replace(/^\d+[.)]\s*/, '').trim();
    if (line.length < 3) continue;

    let name = line;
    let code: string | undefined;
    const codeMatch = line.match(/^(.*?)\s*\(([^()]{1,40})\)\s*$/);
    if (codeMatch && looksLikeProcedureCode(codeMatch[2].trim())) {
      name = codeMatch[1].trim();
      code = codeMatch[2].trim();
    }
    if (name.length < 3) continue;

    const key = normalizeProcedureMatchName(name);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    parsed.push({ name, raw: line, code });
  }
  return parsed;
};

/** Termo seguro para a busca do catálogo (evita quebrar o filtro `or` do PostgREST). */
export const procedureSearchTerm = (name: string): string =>
  String(name || '').replace(/[(),%*]/g, ' ').replace(/\s+/g, ' ').trim();

/**
 * Melhor linha do catálogo para o nome informado, priorizando o profissional
 * da consulta e o convênio (ou particular) da cobrança.
 *
 * A comparação de nome é exata (após normalizar acentos/caixa) porque as
 * observações do agendamento também guardam texto livre — casar por pedaço
 * traria procedimentos que o atendente não lançou.
 */
export const pickBestProcedureMatch = (candidates: any[], opts: ProcedureMatchOptions): any | null => {
  const targets = [opts.name, ...(opts.altNames || [])]
    .map((n) => normalizeProcedureMatchName(n))
    .filter(Boolean);
  if (targets.length === 0) return null;
  const targetCode = String(opts.code || '').trim().toLowerCase();

  let best: { proc: any; score: number } | null = null;

  for (const proc of candidates || []) {
    if (!proc || proc.is_active === false) continue;

    const names = procedureComparableNames(proc);
    const nameMatches = names.some((n) => targets.includes(n));

    const codeMatches = Boolean(
      targetCode && proc.code && String(proc.code).trim().toLowerCase() === targetCode,
    );
    if (!nameMatches && !codeMatches) continue;

    let score = (nameMatches ? 30 : 0) + (codeMatches ? 5 : 0);

    if (opts.professional && procedureBelongsToProfessional(proc, opts.professional)) score += 6;

    if (opts.insuranceId) {
      if (String(proc.insurance_id || '') === String(opts.insuranceId)) score += 8;
      else if (proc.insurance_id) score -= 8;
    } else if (opts.particularOnly) {
      if (!proc.insurance_id) score += 4;
      else score -= 8;
    }

    if (!best || score > best.score) best = { proc, score };
  }

  return best && best.score > 0 ? best.proc : null;
};

/** Preço a usar: preço do convênio quando a linha é de convênio. */
export const procedureEffectivePrice = (proc: any): number => {
  const insurancePrice = Number(proc?.insurance_price);
  const price = Number(proc?.price);
  if (proc?.insurance_id && insurancePrice > 0) return insurancePrice;
  return price > 0 ? price : 0;
};

/** Nome do plano/convênio ou Particular — usado na busca de procedimentos (agenda, orçamento). */
export const procedurePlanLabel = (proc: any): string => {
  const linked = proc?.clinic_insurances?.name;
  if (linked) return String(linked);
  if (proc?.insurance_id) return 'Plano';
  return 'Particular';
};

export const procedureHasInsurancePlan = (proc: any): boolean => Boolean(proc?.insurance_id);
